// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app"
import { getMessaging, getToken, onMessage } from "firebase/messaging"

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID,
}

// Store the current token for comparison — seed from localStorage for persistence across reloads
const FCM_TOKEN_KEY = "fcmToken"
let currentFCMToken = localStorage.getItem(FCM_TOKEN_KEY) || null
let swRegistration = null

// Initialize Firebase
let app = null
let messaging = null

try {
    app = initializeApp(firebaseConfig)
    messaging = getMessaging(app)
} catch (error) {
    console.error("Firebase initialization failed:", error)
}

// Subscribe to foreground messages with a callback
// Returns an unsubscribe function
export function subscribeToMessages(callback) {
    if (!messaging) return () => {}
    return onMessage(messaging, (payload) => {
        callback(payload)
    })
}

// Callback for when token is updated (to be set by the app)
let onTokenRefreshCallback = null

export function setOnTokenRefresh(callback) {
    onTokenRefreshCallback = callback
}

// Check and refresh token periodically (called by app)
export async function checkTokenRefresh() {
    if (!messaging) return null

    // SW not set up yet in this session — delegate to getExistingToken which handles setup
    if (!swRegistration) {
        return getExistingToken()
    }

    try {
        const newToken = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
            serviceWorkerRegistration: swRegistration,
        })

        if (newToken && newToken !== currentFCMToken) {
            console.log("FCM token refreshed")
            currentFCMToken = newToken
            localStorage.setItem(FCM_TOKEN_KEY, newToken)
            if (onTokenRefreshCallback) {
                onTokenRefreshCallback(newToken)
            }
            return newToken
        }
        return null
    } catch (error) {
        console.error("Error checking token refresh:", error)
        return null
    }
}

export async function requestPermission() {
    if (!messaging) return null

    if (!("serviceWorker" in navigator)) {
        console.log("Service Worker is not supported on this browser")
        return null
    }

    if (!("PushManager" in window)) {
        console.log("Push is not supported on this browser")
        return null
    }

    if (!("Notification" in window)) {
        console.log("This browser does not support desktop notification")
        return null
    }

    console.log("Requesting User Permission......")

    if (Notification.permission === "denied") {
        console.log("Notification permission set to deny")
        alert(
            "Notification permission set to deny. Reset permission in browser settings."
        )
        return null
    }

    try {
        const permission = await Notification.requestPermission()
        if (permission !== "granted") {
            console.log("Permission not granted")
            return null
        }

        // Register the service worker and wait for it to be ready
        swRegistration = await navigator.serviceWorker.register(
            "/firebase-messaging-sw.js"
        )
        console.log(
            "Service Worker registered with scope:",
            swRegistration.scope
        )

        // Wait for the service worker to be active
        await navigator.serviceWorker.ready

        // Send Firebase config to the service worker
        if (swRegistration.active) {
            swRegistration.active.postMessage({
                type: "FIREBASE_CONFIG",
                config: firebaseConfig,
            })
            console.log("Firebase config sent to service worker")
        }

        // Get the FCM token, passing our service worker registration
        currentFCMToken = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
            serviceWorkerRegistration: swRegistration,
        })

        if (!currentFCMToken) {
            console.log("Failed to generate the app registration token.")
            return null
        }

        localStorage.setItem(FCM_TOKEN_KEY, currentFCMToken)
        console.log("Client Token: ", currentFCMToken)
        return currentFCMToken
    } catch (error) {
        console.error(
            "An error occurred when requesting to receive the token.",
            error
        )
        return null
    }
}

// Get current token without requesting permission (useful for checking if already registered)
export function getCurrentToken() {
    return currentFCMToken
}

// Remove the stored FCM token — call on logout
export function clearStoredToken() {
    currentFCMToken = null
    localStorage.removeItem(FCM_TOKEN_KEY)
}

// Silently obtain the FCM token if notification permission is already granted.
// Does NOT prompt the user — returns null if permission was never granted or is denied.
export async function getExistingToken() {
    if (!messaging) return null

    if (!("Notification" in window) || Notification.permission !== "granted") {
        return null
    }

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        return null
    }

    try {
        if (!swRegistration) {
            swRegistration = await navigator.serviceWorker.register(
                "/firebase-messaging-sw.js"
            )
            await navigator.serviceWorker.ready

            if (swRegistration.active) {
                swRegistration.active.postMessage({
                    type: "FIREBASE_CONFIG",
                    config: firebaseConfig,
                })
            }
        }

        const token = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
            serviceWorkerRegistration: swRegistration,
        })

        if (token) {
            currentFCMToken = token
            localStorage.setItem(FCM_TOKEN_KEY, token)
            console.log("Existing FCM token obtained:", token)
        }
        return token || null
    } catch (error) {
        console.error("Error obtaining existing FCM token:", error)
        return null
    }
}
